/**
 * Lead Times Dialog
 *
 * Manages the lead times used by the forecast's recommended order dates.
 * Lead times are set per manufacturer (applies to all of that manufacturer's
 * SKUs) with optional per-SKU overrides for exceptions. SKUs with neither fall
 * back to the global default.
 *
 * Props:
 * - open / onClose: dialog visibility
 * - leadTimes: current server state (null while loading)
 * - manufacturers: all known manufacturers (from inventory metadata)
 * - inventoryOptions: inventory items for the override picker
 * - onSaveManufacturer / onSaveInventoryOverride / onDelete: persistence callbacks
 */

import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LeadTimesResponse } from "services/InventoryService";

interface InventoryOption {
  id: string;
  name: string | null;
  skus: string[];
}

interface LeadTimesDialogProps {
  open: boolean;
  onClose: () => void;
  leadTimes: LeadTimesResponse | null;
  manufacturers: string[];
  countries: string[];
  inventoryOptions: InventoryOption[];
  onSaveManufacturer: (manufacturer: string, leadTimeMonths: number) => Promise<void>;
  onSaveInventoryOverride: (inventoryId: string, leadTimeMonths: number) => Promise<void>;
  onSaveCountryAdjustment: (country: string, adjustmentMonths: number) => Promise<void>;
  onDelete: (target: { manufacturer?: string; inventoryId?: string; country?: string }) => Promise<void>;
}

/** Parse a months input; returns null unless it's a positive number. */
function parseMonths(input: string): number | null {
  const value = parseFloat(input);
  return !isNaN(value) && value > 0 ? value : null;
}

/** Parse an adjustment input; may be negative or zero, just has to be a number. */
function parseAdjustment(input: string): number | null {
  const value = parseFloat(input);
  return !isNaN(value) ? value : null;
}

export default function LeadTimesDialog({
  open,
  onClose,
  leadTimes,
  manufacturers,
  countries,
  inventoryOptions,
  onSaveManufacturer,
  onSaveInventoryOverride,
  onSaveCountryAdjustment,
  onDelete,
}: LeadTimesDialogProps): JSX.Element {
  // Staged text inputs keyed by manufacturer, only while a row is being edited
  const [manufacturerInputs, setManufacturerInputs] = useState<Record<string, string>>({});
  const [overrideInputs, setOverrideInputs] = useState<Record<string, string>>({});
  const [countryInputs, setCountryInputs] = useState<Record<string, string>>({});
  const [newOverrideItem, setNewOverrideItem] = useState<InventoryOption | null>(null);
  const [newOverrideMonths, setNewOverrideMonths] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const manufacturerMonths: Record<string, number> = {};
  for (const entry of leadTimes?.manufacturerLeadTimes || []) {
    manufacturerMonths[entry.manufacturer] = entry.leadTimeMonths;
  }

  // Every manufacturer we know about, whether or not it has a lead time yet
  const allManufacturerRows = Array.from(
    new Set([...manufacturers, ...Object.keys(manufacturerMonths)])
  ).sort();

  const countryAdjustmentMonths: Record<string, number> = {};
  for (const entry of leadTimes?.countryAdjustments || []) {
    countryAdjustmentMonths[entry.country] = entry.adjustmentMonths;
  }

  const allCountryRows = Array.from(
    new Set([...countries, ...Object.keys(countryAdjustmentMonths)])
  ).sort();

  const inventoryNameById = new Map(inventoryOptions.map((opt) => [opt.id, opt.name]));

  const runSave = async (action: () => Promise<void>) => {
    setIsSaving(true);
    try {
      await action();
    } finally {
      setIsSaving(false);
    }
  };

  const saveManufacturerRow = (manufacturer: string) => {
    const staged = manufacturerInputs[manufacturer];
    if (staged === undefined) return;
    const months = parseMonths(staged);
    if (months !== null && months !== manufacturerMonths[manufacturer]) {
      runSave(() => onSaveManufacturer(manufacturer, months));
    }
    setManufacturerInputs((prev) => {
      const next = { ...prev };
      delete next[manufacturer];
      return next;
    });
  };

  const saveOverrideRow = (inventoryId: string, currentMonths: number) => {
    const staged = overrideInputs[inventoryId];
    if (staged === undefined) return;
    const months = parseMonths(staged);
    if (months !== null && months !== currentMonths) {
      runSave(() => onSaveInventoryOverride(inventoryId, months));
    }
    setOverrideInputs((prev) => {
      const next = { ...prev };
      delete next[inventoryId];
      return next;
    });
  };

  const saveCountryRow = (country: string) => {
    const staged = countryInputs[country];
    if (staged === undefined) return;
    const adjustment = parseAdjustment(staged);
    if (adjustment !== null && adjustment !== (countryAdjustmentMonths[country] ?? 0)) {
      runSave(() => onSaveCountryAdjustment(country, adjustment));
    }
    setCountryInputs((prev) => {
      const next = { ...prev };
      delete next[country];
      return next;
    });
  };

  const addOverride = () => {
    const months = parseMonths(newOverrideMonths);
    if (!newOverrideItem || months === null) return;
    runSave(() => onSaveInventoryOverride(newOverrideItem.id, months));
    setNewOverrideItem(null);
    setNewOverrideMonths("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Lead Times</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lead time is how long a manufacturer takes from order to arrival, in months.
          It drives each item&apos;s recommended order date. Items without a
          manufacturer default or SKU override use the global default of{" "}
          {leadTimes?.defaultLeadTimeMonths ?? "—"} months.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Manufacturer defaults
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Manufacturer</TableCell>
              <TableCell width={180}>Lead time (months)</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {allManufacturerRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No manufacturers found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {allManufacturerRows.map((manufacturer) => {
              const savedMonths = manufacturerMonths[manufacturer];
              const inputValue =
                manufacturerInputs[manufacturer] ?? (savedMonths != null ? String(savedMonths) : "");
              return (
                <TableRow key={manufacturer}>
                  <TableCell>{manufacturer}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                      placeholder={`default (${leadTimes?.defaultLeadTimeMonths ?? "—"})`}
                      value={inputValue}
                      disabled={isSaving}
                      onChange={(e) =>
                        setManufacturerInputs((prev) => ({ ...prev, [manufacturer]: e.target.value }))
                      }
                      onBlur={() => saveManufacturerRow(manufacturer)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveManufacturerRow(manufacturer);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {savedMonths != null && (
                      <Tooltip title="Remove — this manufacturer's SKUs revert to the global default">
                        <IconButton
                          size="small"
                          disabled={isSaving}
                          onClick={() => runSave(() => onDelete({ manufacturer }))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Geography adjustments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Added on top of the base lead time for everything shipping to that
          country — e.g. +0.5 if the UK takes half a month longer, −0.5 if AU is
          quicker. Countries left blank use the base lead time as-is.
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell width={180}>Adjustment (± months)</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {allCountryRows.map((country) => {
              const savedAdjustment = countryAdjustmentMonths[country];
              const inputValue =
                countryInputs[country] ?? (savedAdjustment != null ? String(savedAdjustment) : "");
              return (
                <TableRow key={country}>
                  <TableCell>{country}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: -12, max: 12, step: 0.5 }}
                      placeholder="0 (same as base)"
                      value={inputValue}
                      disabled={isSaving}
                      onChange={(e) =>
                        setCountryInputs((prev) => ({ ...prev, [country]: e.target.value }))
                      }
                      onBlur={() => saveCountryRow(country)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveCountryRow(country);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {savedAdjustment != null && (
                      <Tooltip title="Remove — this country reverts to the base lead time">
                        <IconButton
                          size="small"
                          disabled={isSaving}
                          onClick={() => runSave(() => onDelete({ country }))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Per-SKU overrides
        </Typography>
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Inventory item</TableCell>
              <TableCell width={180}>Lead time (months)</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {(leadTimes?.inventoryLeadTimes || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No overrides — every item uses its manufacturer default.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(leadTimes?.inventoryLeadTimes || []).map(({ inventoryId, leadTimeMonths }) => (
              <TableRow key={inventoryId}>
                <TableCell>
                  {inventoryNameById.get(inventoryId) || inventoryId}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {inventoryId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                    value={overrideInputs[inventoryId] ?? String(leadTimeMonths)}
                    disabled={isSaving}
                    onChange={(e) =>
                      setOverrideInputs((prev) => ({ ...prev, [inventoryId]: e.target.value }))
                    }
                    onBlur={() => saveOverrideRow(inventoryId, leadTimeMonths)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveOverrideRow(inventoryId, leadTimeMonths);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove — this item reverts to its manufacturer default">
                    <IconButton
                      size="small"
                      disabled={isSaving}
                      onClick={() => runSave(() => onDelete({ inventoryId }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Autocomplete
            size="small"
            sx={{ flex: 1 }}
            options={inventoryOptions}
            value={newOverrideItem}
            onChange={(_, value) => setNewOverrideItem(value)}
            getOptionLabel={(opt) => `${opt.name || opt.id} (${opt.id})`}
            isOptionEqualToValue={(opt, value) => opt.id === value.id}
            renderInput={(params) => <TextField {...params} label="Add override for item" />}
          />
          <TextField
            size="small"
            type="number"
            label="Months"
            sx={{ width: 120 }}
            inputProps={{ min: 0.5, max: 24, step: 0.5 }}
            value={newOverrideMonths}
            onChange={(e) => setNewOverrideMonths(e.target.value)}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={isSaving || !newOverrideItem || parseMonths(newOverrideMonths) === null}
            onClick={addOverride}
          >
            Add
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
